import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listAdminPointsApi } from "api/admin";
import {
  C,
  Panel,
  StatCard,
  SkeletonRows,
  Empty,
  SearchBar,
  Pagination,
  formatDate,
  CsvExportButton,
} from "./admin_ui";

export default function AdminPoints() {
  const [params, setParams] = useSearchParams();
  const search = params.get("q") || "";
  const id = params.get("id") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({
    items: [],
    total: 0,
    sums: { addSum: 0, useSum: 0, paySum: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => setSearchInput(search), [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminPointsApi({
        id: id || undefined,
        search: search || undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success)
        setData({
          items: r.items || [],
          total: r.total || 0,
          sums: r.sums || { addSum: 0, useSum: 0, paySum: 0 },
        });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [search, id, page]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            포인트 거래 내역
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            전체 {data.total.toLocaleString()}건
          </div>
        </div>
        <CsvExportButton kind="points" />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
        }}
      >
        <StatCard
          label="누적 적립"
          value={Number(data.sums.addSum) || 0}
          accent={C.greenDark}
        />
        <StatCard
          label="누적 사용"
          value={Number(data.sums.useSum) || 0}
          accent={C.amber800}
        />
        <StatCard
          label="누적 충전(결제)"
          value={Number(data.sums.paySum) || 0}
          accent={C.blue700}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {id && (
          <button
            type="button"
            onClick={() => setParam("id", "")}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              border: `1px solid ${C.line}`,
              background: "#fff",
              color: C.body,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            ✕ {id} 필터 해제
          </button>
        )}
        <div style={{ flex: 1 }} />
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => setParam("q", searchInput.trim())}
          placeholder="회원 ID / 비고"
        />
      </div>

      <Panel title="거래 내역" count={data.total}>
        {loading ? (
          <SkeletonRows count={8} />
        ) : error ? (
          <Empty label={error} />
        ) : data.items.length === 0 ? (
          <Empty />
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 0.8fr 0.8fr 0.8fr 1fr 1fr 1.2fr",
                gap: 8,
                padding: "10px 4px",
                borderBottom: `1px solid ${C.line}`,
                fontSize: 11,
                fontWeight: 700,
                color: C.sub,
              }}
            >
              <div>회원 ID</div>
              <div style={{ textAlign: "right" }}>적립</div>
              <div style={{ textAlign: "right" }}>사용</div>
              <div style={{ textAlign: "right" }}>충전</div>
              <div style={{ textAlign: "right" }}>잔액</div>
              <div>비고</div>
              <div>일시</div>
            </div>
            {data.items.map((p) => (
              <div
                key={p.docId}
                onClick={() => setParam("id", p.id)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 0.8fr 0.8fr 0.8fr 1fr 1fr 1.2fr",
                  gap: 8,
                  padding: "10px 4px",
                  borderBottom: `1px solid ${C.line}`,
                  fontSize: 12,
                  cursor: "pointer",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    color: C.ink,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.id || "—"}
                </div>
                <div
                  style={{
                    textAlign: "right",
                    color: p.addPoint ? C.greenDark : C.sub,
                    fontWeight: 700,
                  }}
                >
                  {p.addPoint ? `+${p.addPoint}` : "—"}
                </div>
                <div
                  style={{
                    textAlign: "right",
                    color: p.usePoint ? C.amber800 : C.sub,
                    fontWeight: 700,
                  }}
                >
                  {p.usePoint ? `-${p.usePoint}` : "—"}
                </div>
                <div
                  style={{
                    textAlign: "right",
                    color: p.payPoint ? C.blue700 : C.sub,
                    fontWeight: 700,
                  }}
                >
                  {p.payPoint ? `+${p.payPoint}` : "—"}
                </div>
                <div
                  style={{ textAlign: "right", color: C.ink, fontWeight: 700 }}
                >
                  {(Number(p.currentPoint) || 0).toLocaleString()}
                </div>
                <div
                  style={{
                    color: C.sub,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.etc || "—"}
                </div>
                <div style={{ color: C.sub, fontSize: 11 }}>
                  {formatDate(p.createDate)}
                </div>
              </div>
            ))}
          </div>
        )}
        <Pagination
          page={page}
          limit={20}
          total={data.total}
          onChange={(p) => setParam("page", String(p))}
        />
      </Panel>
    </>
  );
}
